import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { Sparkles, ArrowRight } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const servicesData = [
  {
    label: "Media Buying & Planning",
    title: "Omni-Channel Execution Engine",
    desc: "We negotiate, place, and optimise media across TV, digital, print, radio, outdoor, and cinema platforms for maximum ROI and global reach.",
    metric: "Global Execution",
    color: "var(--glow-orange)",
  },
  {
    label: "Performance Marketing",
    title: "Conversion & Revenue Flight",
    desc: "Data-driven performance campaigns focused on Leads, Installs, Conversions, Revenue Growth, and FTDs across search, social, and programmatic DSPs.",
    metric: "High-ROI Attainments",
    color: "var(--glow-purple)",
  },
  {
    label: "Gaming & iGaming Marketing",
    title: "User Acquisition at Scale",
    desc: "High-velocity player acquisition strategies, app install funnels, and retention models engineered specifically for gaming and entertainment platforms.",
    metric: "Scalable FTDs",
    color: "var(--glow-cyan)",
  },
  {
    label: "Digital & Traditional Synergy",
    title: "Full-Funnel Cross-Market Reach",
    desc: "Seamless integration connecting digital performance tracking with traditional TV, OOH, and radio media buying across Asia, UAE, and Europe.",
    metric: "6+ Global Markets",
    color: "var(--glow-orange)",
  },
];

// Catmull-Rom spline points for the 3D tube path
const rawPoints = [
  [10, 89, 0],
  [50, 88, 10],
  [76, 139, 20],
  [126, 141, 12],
  [150, 112, 8],
  [157, 73, 0],
  [180, 44, 5],
  [207, 35, 10],
  [232, 36, 0],
];

export function InteractiveServices() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [activeCard, setActiveCard] = useState<number>(-1);

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const ww = window.innerWidth;
    const wh = window.innerHeight;

    // --- THREE.JS SETUP ---
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      alpha: false,
    });
    renderer.setSize(ww, wh);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));

    const scene = new THREE.Scene();
    // Deep dark space blue fog matching the CodePen space atmosphere
    scene.fog = new THREE.Fog(0x01020b, 15, 95);

    const cameraGroup = new THREE.Group();
    const camera = new THREE.PerspectiveCamera(45, ww / wh, 0.01, 300);
    camera.rotation.y = Math.PI;
    cameraGroup.add(camera);
    scene.add(cameraGroup);

    // Get max anisotropic filtering capability for razor-sharp textures at grazing angles
    const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

    // --- HIGH-FIDELITY COSMIC NEBULA & STARFIELD TEXTURE ---
    const createSpaceTexture = () => {
      const w = 2048;
      const h = 1024;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      
      if (ctx) {
        // Deep indigo space void base
        ctx.fillStyle = "#01020c";
        ctx.fillRect(0, 0, w, h);

        // Soft, organic glowing nebula clouds (vibrant space blues, cyans, and cosmic purples)
        const nebulaColors = [
          { r: 0, g: 130, b: 255, a: 0.28 },   // Deep Space Blue
          { r: 139, g: 92, b: 246, a: 0.22 },  // Soft Violet
          { r: 0, g: 230, b: 255, a: 0.20 },   // Electric Cyan
          { r: 168, g: 85, b: 247, a: 0.18 }   // Cosmic Purple
        ];
        
        ctx.globalCompositeOperation = "screen";
        for (let i = 0; i < 75; i++) {
          const x = Math.random() * w;
          const y = Math.random() * h;
          const r = Math.random() * 450 + 150;
          
          const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
          const c = nebulaColors[Math.floor(Math.random() * nebulaColors.length)];
          grad.addColorStop(0, `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`);
          grad.addColorStop(0.3, `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a * 0.2})`);
          grad.addColorStop(1, "rgba(0,0,0,0)");
          
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }

        // Dense, razor-sharp starfield
        ctx.globalCompositeOperation = "source-over";
        for (let i = 0; i < 900; i++) {
          const x = Math.random() * w;
          const y = Math.random() * h;
          const r = Math.random() * 0.9 + 0.15;
          const alpha = Math.random() * 0.85 + 0.15;
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }

        // Twinkling stars with soft halo glows
        for (let i = 0; i < 30; i++) {
          const x = Math.random() * w;
          const y = Math.random() * h;
          const r = Math.random() * 1.3 + 1.0;
          
          ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();

          const glowGrad = ctx.createRadialGradient(x, y, 0, x, y, r * 10);
          glowGrad.addColorStop(0, "rgba(255, 255, 255, 0.22)");
          glowGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
          ctx.fillStyle = glowGrad;
          ctx.beginPath();
          ctx.arc(x, y, r * 10, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      
      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(12, 2);
      texture.anisotropy = maxAnisotropy;
      return texture;
    };

    const fallbackSpaceTexture = createSpaceTexture();

    // --- CREATE PATH AND TUBE ---
    const points = rawPoints.map((pt) => new THREE.Vector3(pt[0], pt[2], pt[1]));
    const path = new THREE.CatmullRomCurve3(points);
    path.tension = 0.5;

    // Outer tube mesh (BackSide)
    const tubeGeometry = new THREE.TubeGeometry(path, 300, 5, 24, false);
    const tubeMaterial = new THREE.MeshPhongMaterial({
      side: THREE.BackSide,
      map: fallbackSpaceTexture,
      shininess: 25,
      specular: 0x224488, // Blue specular reflection to glisten under point light
      emissive: 0x000000,
    });
    const tubeMesh = new THREE.Mesh(tubeGeometry, tubeMaterial);
    scene.add(tubeMesh);

    // Procedural Bump Map for space ripples (100% local, zero external network requests)
    const createProceduralBumpTexture = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#808080";
        ctx.fillRect(0, 0, 512, 512);
        for (let y = 0; y < 512; y += 4) {
          const val = Math.floor(128 + Math.sin((y / 512) * Math.PI * 16) * 35);
          ctx.fillStyle = `rgb(${val}, ${val}, ${val})`;
          ctx.fillRect(0, y, 512, 4);
        }
      }
      const bumpTex = new THREE.CanvasTexture(canvas);
      bumpTex.wrapS = THREE.RepeatWrapping;
      bumpTex.wrapT = THREE.RepeatWrapping;
      bumpTex.repeat.set(15, 2);
      bumpTex.anisotropy = maxAnisotropy;
      return bumpTex;
    };

    tubeMaterial.bumpMap = createProceduralBumpTexture();
    tubeMaterial.bumpScale = -0.02;
    tubeMaterial.needsUpdate = true;

    // Inner wireframe tube - very fine and subtle ice blue radar grid lines
    const wireGeometry = new THREE.TubeGeometry(path, 150, 4.85, 12, false);
    const wireframeGeo = new THREE.EdgesGeometry(wireGeometry);
    const wireMaterial = new THREE.LineBasicMaterial({
      color: 0x88e5ff, // Crisp ice blue/cyan grid lines
      transparent: true,
      opacity: 0.05,  // Subtler grid lines to let space background dominate
    });
    const wireframeMesh = new THREE.LineSegments(wireframeGeo, wireMaterial);
    scene.add(wireframeMesh);

    // --- REALISTIC DIM LIGHTING SYSTEM ---
    const pointLightWhite = new THREE.PointLight(0xffffff, 0.55, 14, 1.0);
    scene.add(pointLightWhite);

    // Ambient space colored point lights (very subtle blue/purple tinting, far away, low intensity)
    const lightCyan = new THREE.PointLight(0x00f0ff, 0.9, 25, 1.2);
    const lightPurple = new THREE.PointLight(0xa855f7, 0.75, 25, 1.2);
    scene.add(lightCyan);
    scene.add(lightPurple);

    // Extremely dim ambient light to simulate distant stars
    const ambientLight = new THREE.AmbientLight(0x05040a, 0.1);
    scene.add(ambientLight);

    // --- DUST PARTICLES ---
    const createParticles = () => {
      const pCount = 1000;
      const geom = new THREE.BufferGeometry();
      const pos = new Float32Array(pCount * 3);

      for (let i = 0; i < pCount * 3; i += 3) {
        pos[i] = Math.random() * 500 - 250;
        pos[i + 1] = Math.random() * 60 - 30;
        pos[i + 2] = Math.random() * 500 - 250;
      }

      geom.setAttribute("position", new THREE.BufferAttribute(pos, 3));

      const pCanvas = document.createElement("canvas");
      pCanvas.width = 16;
      pCanvas.height = 16;
      const pCtx = pCanvas.getContext("2d");
      if (pCtx) {
        const radGrad = pCtx.createRadialGradient(8, 8, 0, 8, 8, 8);
        radGrad.addColorStop(0, "rgba(255, 255, 255, 1)");
        radGrad.addColorStop(0.5, "rgba(161, 52, 235, 0.4)");
        radGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
        pCtx.fillStyle = radGrad;
        pCtx.beginPath();
        pCtx.arc(8, 8, 8, 0, Math.PI * 2);
        pCtx.fill();
      }
      const pTexture = new THREE.CanvasTexture(pCanvas);

      const mat = new THREE.PointsMaterial({
        color: 0xffffff,
        size: 1.2,
        map: pTexture,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });

      const pPoints = new THREE.Points(geom, mat);
      scene.add(pPoints);
      return pPoints;
    };

    const particleSystem = createParticles();

    // --- POSTPROCESSING (Bloom) ---
    const renderScene = new RenderPass(scene, camera);
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth / 2, window.innerHeight / 2),
      1.4, // Downscaled bloom strength for rendering speed
      0.3, // Bloom threshold
      0.85 // Bloom radius
    );
    const composer = new EffectComposer(renderer);
    composer.setSize(window.innerWidth, window.innerHeight);
    composer.addPass(renderScene);
    composer.addPass(bloomPass);

    // --- UPDATE POSITION FUNCTION ---
    const lookAheadPct = 0.015;
    const updateCamera = (pct: number) => {
      const clampedPct = Math.max(0, Math.min(0.999 - lookAheadPct, pct));
      const p1 = path.getPointAt(clampedPct);
      const p2 = path.getPointAt(clampedPct + lookAheadPct);

      cameraGroup.position.copy(p1);
      cameraGroup.lookAt(p2);

      // Primary white light slightly in front of the camera (look-ahead)
      pointLightWhite.position.copy(p2);

      // Cyan light goes ahead of camera, purple light stays slightly behind camera
      const pAhead = path.getPointAt(Math.min(0.999, clampedPct + 0.04));
      const pBehind = path.getPointAt(Math.max(0, clampedPct - 0.02));
      lightCyan.position.copy(pAhead);
      lightPurple.position.copy(pBehind);
    };

    // Parallax mouse movements
    let targetRotX = 0;
    let targetRotY = 0;
    let currentRotX = 0;
    let currentRotY = 0;

    const onMouseMove = (e: MouseEvent) => {
      targetRotX = ((e.clientX / window.innerWidth) - 0.5) * 0.12;
      targetRotY = ((e.clientY / window.innerHeight) - 0.5) * 0.12;
    };
    window.addEventListener("mousemove", onMouseMove);

    // Initial camera position
    updateCamera(0);

    // --- GSAP SCROLL TRIGGER ---
    let cameraPercent = 0;
    let isRenderable = false;
    
    // Visibility boundary trigger: only render when the Services section is in viewport
    const visibilityTrigger = ScrollTrigger.create({
      trigger: containerRef.current,
      start: "top bottom",
      end: "bottom top",
      onToggle: (self) => {
        isRenderable = self.isActive;
      },
    });

    const scrollTrigger = ScrollTrigger.create({
      trigger: containerRef.current,
      start: "top top",
      end: "bottom bottom",
      scrub: 0.3,
      onUpdate: (self) => {
        const progress = self.progress;
        cameraPercent = progress * 0.98;

        let activeIdx = -1;
        if (progress > 0.05 && progress < 0.25) activeIdx = 0;
        else if (progress >= 0.28 && progress < 0.48) activeIdx = 1;
        else if (progress >= 0.51 && progress < 0.71) activeIdx = 2;
        else if (progress >= 0.74 && progress < 0.94) activeIdx = 3;

        setActiveCard(activeIdx);
      },
    });

    // --- RENDER LOOP ---
    let animationId = 0;
    const render = () => {
      animationId = requestAnimationFrame(render);
      if (!isRenderable) return;

      // Animate active texture offset
      if (tubeMaterial.map) {
        tubeMaterial.map.offset.x -= 0.0008;
      }

      particleSystem.rotation.y += 0.0001;
      particleSystem.rotation.x += 0.00005;

      updateCamera(cameraPercent);

      currentRotX += (targetRotX - currentRotX) * 0.05;
      currentRotY += (targetRotY - currentRotY) * 0.05;
      camera.rotation.y = Math.PI - currentRotX;
      camera.rotation.x = -currentRotY;

      composer.render();
    };

    render();

    // --- RESIZE LISTENER ---
    const onResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      composer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    // --- CLEANUP ---
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(animationId);
      visibilityTrigger.kill();
      scrollTrigger.kill();
      
      // Dispose of resources
      tubeGeometry.dispose();
      tubeMaterial.dispose();
      if (tubeMaterial.map) tubeMaterial.map.dispose();
      if (tubeMaterial.bumpMap) tubeMaterial.bumpMap.dispose();
      wireGeometry.dispose();
      wireframeGeo.dispose();
      wireMaterial.dispose();
      particleSystem.geometry.dispose();
      (particleSystem.material as THREE.Material).dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <section
      ref={containerRef}
      id="services"
      className="relative w-full h-[500vh] bg-black"
    >
      {/* Sticky Viewport */}
      <div className="sticky top-0 w-full h-screen overflow-hidden flex flex-col items-center justify-center">
        {/* Canvas */}
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full block" />

        {/* Ambient Dark Vignette Overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,transparent_45%,rgba(0,0,0,0.95)_120%)] pointer-events-none z-10" />

        {/* Title Header (shows when not looking at cards) */}
        <div 
          className={`absolute top-28 text-center px-4 z-20 pointer-events-none transition-all duration-700 ease-in-out ${
            activeCard === -1 ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
          }`}
        >
          <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.25em] text-muted-foreground bg-white/5">
            <Sparkles className="h-3 w-3 text-[var(--glow-orange)]" />
            Capabilities & Services
          </div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
            Explore Our <span className="text-gradient">Growth Engine</span>
          </h2>
          <p className="mt-3 text-sm md:text-base text-muted-foreground/80 max-w-md mx-auto">
            Scroll down to fly through our core capabilities and see how we scale digital brands.
          </p>
        </div>

        {/* Interactive Storytelling Cards Overlay */}
        <div className="absolute inset-0 flex items-center justify-center px-4 md:px-6 z-25 pointer-events-none">
          {servicesData.map((s, idx) => {
            const isActive = activeCard === idx;
            return (
              <div
                key={idx}
                className={`absolute w-full max-w-xl glass-strong ring-glow p-6 sm:p-8 md:p-10 rounded-3xl transition-all duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] pointer-events-auto select-none ${
                  isActive
                    ? "opacity-100 scale-100 translate-y-0 filter-none"
                    : "opacity-0 scale-95 translate-y-12 blur-sm pointer-events-none"
                }`}
                style={{
                  borderLeft: `3px solid ${s.color}`,
                }}
              >
                <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-white">
                  {s.label}
                </h3>
                <h4 className="text-sm font-medium text-muted-foreground/90 mt-1">
                  {s.title}
                </h4>
                
                <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                  {s.desc}
                </p>

                <div className="mt-6 flex items-center gap-2">
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white hover:text-[var(--glow-orange)] transition"
                  >
                    Start Project <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Scroll Indicator */}
        <div 
          className={`absolute bottom-8 flex flex-col items-center gap-2 transition-all duration-500 z-20 pointer-events-none ${
            activeCard === servicesData.length - 1 ? "opacity-0 translate-y-4" : "opacity-100"
          }`}
        >
          <div className="w-5 h-9 rounded-full border border-white/20 flex justify-center p-1 bg-black/10 backdrop-blur-sm">
            <div className="w-1 h-2 bg-white/60 rounded-full animate-[bounce_1.5s_infinite]" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/60">
            Scroll to Navigate
          </span>
        </div>
      </div>
    </section>
  );
}
